"use client";

import { type AbstractIntlMessages, NextIntlClientProvider } from "next-intl";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { LOCALE_KEY } from "@/constants/app.constants";
import { defaultLocale, type Locale, isValidLocale, timeZone } from "@/i18n/config";
import { loadMessageBundle, MESSAGE_BUNDLES, mergeMessages, type MessageBundle } from "@/i18n/messageBundles";
import enMessages from "@/locales/en/common.json";
import { XError } from "@/utils/error.utils";
import { reportClientWarning } from "@/lib/clientReporting";
const MESSAGE_LOADERS: Record<Locale, () => Promise<AbstractIntlMessages>> = {
  en: async () => enMessages,
  es: () => import("@/locales/es/common.json").then(mod => mod.default)
};
type LoadedBundles = Partial<Record<MessageBundle, AbstractIntlMessages>>;
type ActiveLocale = {
  locale: Locale;
  messages: AbstractIntlMessages;
  bundles: LoadedBundles;
};
type MessageBundleStatus = "loading" | "ready" | "failed";
type LocaleContextType = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  bundleStatus: (bundle: MessageBundle) => MessageBundleStatus;
  requestBundle: (bundle: MessageBundle) => void;
};
const LocaleContext = createContext<LocaleContextType | undefined>(undefined);
export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new XError({
      message: "useLocale must be used within LocaleProvider"
    });
  }
  return context;
}
export function useMessageBundles(bundles: readonly MessageBundle[]) {
  const {
    locale,
    bundleStatus,
    requestBundle
  } = useLocale();
  const statuses = bundles.map(bundle => bundleStatus(bundle));
  const loadingKey = bundles.filter((_, index) => statuses[index] === "loading").join(",");
  useEffect(() => {
    const loading = new Set(loadingKey.split(","));
    for (const bundle of MESSAGE_BUNDLES) {
      if (loading.has(bundle)) requestBundle(bundle);
    }
  }, [loadingKey, locale, requestBundle]);
  const status: MessageBundleStatus = statuses.includes("failed") ? "failed" : statuses.every(each => each === "ready") ? "ready" : "loading";
  const retry = () => bundles.forEach((bundle, index) => {
    if (statuses[index] !== "ready") requestBundle(bundle);
  });
  return {
    status,
    retry
  };
}
function reportBundleFailure({
  error,
  locale,
  bundle
}: {
  error: unknown;
  locale: Locale;
  bundle?: MessageBundle;
}) {
  reportClientWarning({
    message: "Locale catalog download failed",
    cause: error instanceof Error ? error : null,
    data: {
      locale,
      bundle
    },
    tags: {
      surface: "localeProvider",
      locale
    }
  });
}
export function resolveStoredLocale(stored: string | null): Locale {
  if (stored && isValidLocale(stored)) {
    return stored;
  }
  return defaultLocale;
}
export function collectSwitchedBundles({
  bundleNames,
  settled
}: {
  bundleNames: readonly MessageBundle[];
  settled: readonly PromiseSettledResult<AbstractIntlMessages>[];
}): {
  isComplete: true;
  bundles: LoadedBundles;
} | {
  isComplete: false;
  failures: {
    bundle: MessageBundle;
    error: unknown;
  }[];
} {
  const bundles: LoadedBundles = {};
  const failures: {
    bundle: MessageBundle;
    error: unknown;
  }[] = [];
  settled.forEach((result, index) => {
    const bundle = bundleNames[index];
    if (result.status === "fulfilled") bundles[bundle] = result.value;else failures.push({
      bundle,
      error: result.reason
    });
  });
  return failures.length > 0 ? {
    isComplete: false,
    failures
  } : {
    isComplete: true,
    bundles
  };
}
export function LocaleProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [active, setActive] = useState<ActiveLocale>({
    locale: defaultLocale,
    messages: enMessages,
    bundles: {}
  });
  const [failedBundles, setFailedBundles] = useState<ReadonlySet<MessageBundle>>(() => new Set());
  const latestRequestedLocale = useRef<Locale>(defaultLocale);
  const appliedLocale = useRef<Locale>(defaultLocale);
  const requestedBundles = useRef(new Set<MessageBundle>());
  const markBundleFailed = useCallback(({
    bundle,
    hasFailed
  }: {
    bundle: MessageBundle;
    hasFailed: boolean;
  }) => setFailedBundles(previous => {
    if (previous.has(bundle) === hasFailed) return previous;
    const next = new Set(previous);
    if (hasFailed) next.add(bundle);else next.delete(bundle);
    return next;
  }), []);
  const applyLocale = useCallback(async (requested: Locale) => {
    latestRequestedLocale.current = requested;
    const loadMissing = (loaded: LoadedBundles) => {
      const bundleNames = [...requestedBundles.current].filter(bundle => !loaded[bundle]);
      return Promise.allSettled(bundleNames.map(bundle => loadMessageBundle({
        locale: requested,
        bundle
      }))).then(settled => collectSwitchedBundles({
        bundleNames,
        settled
      }));
    };
    const firstRound = loadMissing({});
    let messages: AbstractIntlMessages;
    try {
      messages = await MESSAGE_LOADERS[requested]();
    } catch (error) {
      reportBundleFailure({
        error,
        locale: requested
      });
      if (latestRequestedLocale.current === requested) {
        latestRequestedLocale.current = appliedLocale.current;
      }
      return;
    }
    const bundles: LoadedBundles = {};
    let round = firstRound;
    for (;;) {
      const outcome = await round;
      if (latestRequestedLocale.current !== requested) return;
      if (!outcome.isComplete) {
        for (const {
          bundle,
          error
        } of outcome.failures) {
          reportBundleFailure({
            error,
            locale: requested,
            bundle
          });
        }
        latestRequestedLocale.current = appliedLocale.current;
        return;
      }
      Object.assign(bundles, outcome.bundles);
      const isEveryWindowCovered = [...requestedBundles.current].every(bundle => bundles[bundle]);
      if (isEveryWindowCovered) break;
      round = loadMissing(bundles);
    }
    appliedLocale.current = requested;
    setActive(previous => previous.locale === requested ? previous : {
      locale: requested,
      messages,
      bundles
    });
  }, []);
  const requestBundle = useCallback((bundle: MessageBundle) => {
    requestedBundles.current.add(bundle);
    const locale = appliedLocale.current;
    markBundleFailed({
      bundle,
      hasFailed: false
    });
    loadMessageBundle({
      locale,
      bundle
    }).then(bundleMessages => setActive(previous => previous.locale !== locale || previous.bundles[bundle] ? previous : {
      ...previous,
      bundles: {
        ...previous.bundles,
        [bundle]: bundleMessages
      }
    })).catch(error => {
      reportBundleFailure({
        error,
        locale,
        bundle
      });
      markBundleFailed({
        bundle,
        hasFailed: true
      });
    });
  }, [markBundleFailed]);
  const bundleStatus = useCallback((bundle: MessageBundle): MessageBundleStatus => {
    if (active.bundles[bundle]) return "ready";
    return failedBundles.has(bundle) ? "failed" : "loading";
  }, [active.bundles, failedBundles]);
  useEffect(() => {
    void applyLocale(resolveStoredLocale(localStorage.getItem(LOCALE_KEY)));
  }, [applyLocale]);
  const setLocale = useCallback((newLocale: Locale) => {
    if (isValidLocale(newLocale)) {
      localStorage.setItem(LOCALE_KEY, newLocale);
      void applyLocale(newLocale);
    }
  }, [applyLocale]);
  const messages = useMemo(() => Object.values(active.bundles).reduce<AbstractIntlMessages>((merged, bundleMessages) => bundleMessages ? mergeMessages(merged, bundleMessages) : merged, active.messages), [active.messages, active.bundles]);
  const contextValue = useMemo(() => ({
    locale: active.locale,
    setLocale,
    bundleStatus,
    requestBundle
  }), [active.locale, setLocale, bundleStatus, requestBundle]);
  return <LocaleContext.Provider value={contextValue} data-sentry-element="LocaleContext.Provider" data-sentry-component="LocaleProvider" data-sentry-source-file="LocaleProvider.tsx">
            <NextIntlClientProvider locale={active.locale} messages={messages} timeZone={timeZone} data-sentry-element="NextIntlClientProvider" data-sentry-source-file="LocaleProvider.tsx">
                {children}
            </NextIntlClientProvider>
        </LocaleContext.Provider>;
}